import React, { useCallback, useEffect, useRef, useState } from 'react';
import distributorService from '../services/distributorService';
import DistributorBankAlerts from '../components/DistributorBankAlerts';
import DistributorDeliveryQueue from '../components/distributor-admin/DistributorDeliveryQueue';
import BankAlertDeliveryQueue from '../components/distributor-admin/BankAlertDeliveryQueue';
import DistributorBankEntry from '../components/distributor-admin/DistributorBankEntry';
import DistributorAchRecovery, {
  DistributorAchMonitorHealth,
} from '../components/distributor-admin/DistributorAchRecovery';
import DistributorCardRecovery from '../components/distributor-admin/DistributorCardRecovery';
import DistributorReleaseMonitorHealth from '../components/distributor-admin/DistributorReleaseMonitorHealth';
import DistributorOperationalNotices from '../components/distributor/DistributorOperationalNotices';
import OperationalNoticeDeliveryQueue from '../components/distributor-admin/OperationalNoticeDeliveryQueue';
import { validBankFamilies } from '../components/distributor/distributorBankSummary';
import useShippingRequest from '../hooks/useShippingRequest';
import CashBankBoundary from '../components/distributor-cash/CashBankBoundary';
import CashAdminWorkspace from '../components/distributor-cash/CashAdminWorkspace';
import '../styles/distributor-admin.css';

const unwrap = (response) => response?.data?.data;
const validOptions = (data) => Array.isArray(data?.distributors) && Array.isArray(data?.products);

export default function AdminDistributorBank() {
  const [options, setOptions] = useState(null);
  const [distributorId, setDistributorId] = useState('');
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [fundingPending, setFundingPending] = useState(false);
  const [fundingMethod, setFundingMethod] = useState(null);
  const [cashLocked, setCashLocked] = useState(false);
  const [cashRefresh, setCashRefresh] = useState(0);
  const refreshCash = useCallback(() => setCashRefresh((value) => value + 1), []);
  const cashLockedRef = useRef(false);
  const onCashLockChange = useCallback((locked) => {
    cashLockedRef.current = locked;
    setCashLocked(locked);
  }, []);
  const fundingPendingRef = useRef(false);
  const onFundingPendingChange = useCallback((pending) => {
    fundingPendingRef.current = pending;
    setFundingPending(pending);
  }, []);
  const [entryState, setEntryState] = useState({ busy: false, pending: false });
  const entryStateRef = useRef(entryState);
  const onEntryStateChange = useCallback((state) => {
    entryStateRef.current = state;
    setEntryState(state);
  }, []);
  const [mappingReview, setMappingReview] = useState(false);
  const [productId, setProductId] = useState('');
  const [familyKey, setFamilyKey] = useState('');
  const busyRef = useRef(false);
  const { begin: beginOptions, version: optionsVersion } = useShippingRequest('admin-bank-options');
  const { begin: beginDetails, version: detailsVersion } = useShippingRequest(distributorId);
  const loadOptions = useCallback(async () => {
    if (
      busyRef.current ||
      entryStateRef.current.busy ||
      fundingPendingRef.current ||
      cashLockedRef.current
    )
      return;
    const request = beginOptions();
    try {
      const data = unwrap(await distributorService.adminBankOptions());
      if (!validOptions(data)) throw new Error('Invalid options');
      if (request.isCurrent()) {
        setOptions(data);
        setMappingReview(false);
        setError('');
      }
    } catch {
      if (request.isCurrent()) setError('Bank administration is unavailable. Please retry.');
    }
  }, [beginOptions]);
  useEffect(() => {
    busyRef.current = false;
    setBusy(false);
    setOptions(null);
    onFundingPendingChange(false);
    setFundingMethod(null);
    setDistributorId('');
    onEntryStateChange({ busy: false, pending: false });
    setProductId('');
    setFamilyKey('');
    setNotice('');
    loadOptions();
  }, [loadOptions, optionsVersion, onEntryStateChange, onFundingPendingChange]);
  const fetchDetails = async (request, id, nextPage) => {
    const [balance, entries] = await Promise.all([
      distributorService.adminBankSummary(id),
      distributorService.adminBankHistory(id, nextPage),
    ]);
    const nextSummary = unwrap(balance),
      nextHistory = unwrap(entries);
    if (
      !validBankFamilies(nextSummary?.families) ||
      !Array.isArray(nextHistory?.data) ||
      !Number.isSafeInteger(nextHistory.total) ||
      nextHistory.total < 0
    )
      throw new Error('Invalid bank response');
    if (!request.isCurrent()) return;
    setSummary(nextSummary);
    setHistory(nextHistory);
    setPage(nextPage);
  };
  const refresh = async (nextPage = page) => {
    if (busyRef.current || !distributorId) return;
    const request = beginDetails();
    setLoading(true);
    setSummary(null);
    setHistory(null);
    try {
      await fetchDetails(request, distributorId, nextPage);
      if (request.isCurrent()) setError('');
    } catch {
      if (request.isCurrent()) setError('Bank details are unavailable. Please retry.');
    } finally {
      if (request.isCurrent()) setLoading(false);
    }
  };
  useEffect(() => {
    setSummary(null);
    setHistory(null);
    setError('');
    setNotice('');
    setPage(1);
    if (distributorId) refresh(1);
    // Each selection/scope change starts a new fenced read; page changes use refresh explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributorId, detailsVersion]);
  const selectedProduct = options?.products.find((product) => product._id === productId);
  const chooseProduct = (id) => {
    setProductId(id);
    setFamilyKey(options?.products.find((product) => product._id === id)?.familyKey || '');
  };
  const saveMapping = async (event) => {
    event.preventDefault();
    if (
      busyRef.current ||
      fundingPendingRef.current ||
      entryStateRef.current.busy ||
      entryStateRef.current.pending ||
      mappingReview ||
      loading ||
      cashLocked
    )
      return;
    if (!selectedProduct || (familyKey && !/^[a-z][a-z0-9_]{1,39}$/.test(familyKey))) {
      setError('Choose a product and enter a valid family key.');
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    const request = beginOptions();
    const captured = {
      productId,
      familyKey: familyKey || null,
      expectedFamilyKey: selectedProduct.familyKey,
    };
    let saved = false;
    try {
      const receipt = unwrap(
        await distributorService.mapBankProduct(captured.productId, {
          expectedFamilyKey: captured.expectedFamilyKey,
          familyKey: captured.familyKey,
        })
      );
      if (!request.isCurrent()) return;
      if (receipt?.productId !== captured.productId || receipt.familyKey !== captured.familyKey)
        throw new Error('Invalid mapping receipt');
      saved = true;
      const data = unwrap(await distributorService.adminBankOptions());
      if (!validOptions(data)) throw new Error('Invalid products');
      if (!request.isCurrent()) return;
      setOptions(data);
      setNotice('Product family saved.');
      if (distributorId) await fetchDetails(beginDetails(), distributorId, 1);
    } catch {
      if (!request.isCurrent()) return;
      setMappingReview(true);
      setError(
        saved
          ? 'Product mapping was saved, but the list could not refresh. Reload the list.'
          : 'Product mapping could not be confirmed. Reload the list and review the current family before retrying.'
      );
    } finally {
      if (request.isCurrent()) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };

  return (
    <main
      className="me-distributor-admin mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8"
      aria-label="Distributor bank administration"
    >
      <header className="me-distributor-admin-header">
        <p className="me-distributor-admin-eyebrow">Distributor operations</p>
        <h1 className="text-3xl font-semibold text-slate-900">Distributor bank</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">
          Review balances, funding activity, and product access.
        </p>
      </header>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="text-green-700">
          {notice}
        </p>
      )}
      {!options && (
        <button type="button" onClick={loadOptions}>
          Retry loading distributors and products
        </button>
      )}
      {options && (
        <>
          <div className="me-distributor-admin-selector">
            <label className="block">
              Distributor{' '}
              <select
                disabled={busy || entryState.busy || fundingPending || cashLocked}
                className="block w-full max-w-md rounded border p-2"
                value={distributorId}
                onChange={(event) => {
                  if (
                    busyRef.current ||
                    entryStateRef.current.busy ||
                    fundingPendingRef.current ||
                    cashLockedRef.current
                  )
                    return;
                  onEntryStateChange({ busy: false, pending: false });
                  setDistributorId(event.target.value);
                }}
              >
                <option value="">Choose distributor</option>
                {options.distributors.map((row) => (
                  <option key={row._id} value={row._id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-sm text-slate-600">
              Review the selected distributor's balance and funding history below.
            </p>
          </div>
          {distributorId && (
            <h2 className="text-lg font-semibold">
              Selected distributor:{' '}
              {options.distributors.find((row) => row._id === distributorId)?.name}
            </h2>
          )}
          {distributorId && (
            <div className="me-distributor-admin-bank-grid">
              <CashBankBoundary
                key={`cash-bank:${distributorId}:${cashRefresh}`}
                distributorId={distributorId}
                admin
              />
              <DistributorBankAlerts
                key={`cash-alerts:${distributorId}`}
                cash
                distributorId={distributorId}
                readOnly={cashLocked || busy}
              />
            </div>
          )}
          {distributorId && (
            <CashAdminWorkspace
              key={`cash-settings:${distributorId}`}
              distributorId={distributorId}
              disabled={busy || entryState.busy || entryState.pending || fundingPending || loading}
              onLockChange={onCashLockChange}
              onBankChange={refreshCash}
            />
          )}
          <details className="me-distributor-admin-card">
            <summary className="font-semibold">Historical unit bank tools</summary>
            <p className="my-3 text-sm text-slate-600">
              Historical unit records and reconciliation. These units are separate from Tissue Bank
              Credit and are not converted into dollars.
            </p>
            <div className="me-distributor-admin-bank-grid">
              <section
                className={`me-distributor-admin-card space-y-4 ${distributorId ? '' : 'me-distributor-admin-wide'}`}
                aria-label="Product bank mapping"
              >
                <h2 className="text-lg font-semibold text-slate-900">Shared product families</h2>
                <p className="text-sm text-slate-600">
                  This mapping applies to the shared MedEffects product catalog and all distributor
                  banks, not only the selected distributor. Existing history keeps its recorded
                  family.
                </p>
                {mappingReview && (
                  <button type="button" disabled={busy} onClick={loadOptions}>
                    Reload product families
                  </button>
                )}
                <form onSubmit={saveMapping}>
                  <fieldset
                    disabled={
                      busy ||
                      entryState.busy ||
                      entryState.pending ||
                      mappingReview ||
                      fundingPending ||
                      cashLocked
                    }
                    className="grid gap-3 sm:grid-cols-2"
                  >
                    <label>
                      Product{' '}
                      <select
                        disabled={busy}
                        className="block rounded border p-2"
                        value={productId}
                        onChange={(event) => chooseProduct(event.target.value)}
                      >
                        <option value="">Choose product</option>
                        {options.products.map((row) => (
                          <option key={row._id} value={row._id}>
                            {row.name} {row.familyKey ? `(${row.familyKey})` : '(unmapped)'}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Family key{' '}
                      <input
                        className="block rounded border p-2"
                        value={familyKey}
                        onChange={(event) => setFamilyKey(event.target.value)}
                        placeholder="membrane_wrap"
                      />
                    </label>
                    <button
                      type="submit"
                      disabled={busy || !productId}
                      className="me-distributor-admin-primary sm:justify-self-start"
                    >
                      Save family
                    </button>
                  </fieldset>
                </form>
              </section>
              {distributorId && (
                <>
                  <section
                    className="me-distributor-admin-card space-y-4"
                    aria-label="Distributor balances"
                  >
                    <h2 className="text-lg font-semibold text-slate-900">
                      Recorded historical unit balances
                    </h2>
                    {!summary && !error && <p>Loading balances...</p>}
                    {error && (
                      <button type="button" onClick={() => refresh()}>
                        Retry bank details
                      </button>
                    )}
                    {summary?.families.length === 0 && <p>No mapped families or entries yet.</p>}
                    {summary?.families.map((family) => (
                      <p key={family.key}>
                        {family.key}: <strong>{family.balance.toLocaleString()} units</strong>{' '}
                        recorded
                        {family.spendingBlocked && (
                          <span>
                            {' '}
                            · Spending paused · Recorded balance: {family.balance.toLocaleString()}{' '}
                            units
                          </span>
                        )}
                      </p>
                    ))}
                  </section>
                  <DistributorBankEntry
                    key={distributorId}
                    distributorId={distributorId}
                    summary={summary}
                    disabled={busy || loading || mappingReview || fundingPending || cashLocked}
                    onStateChange={onEntryStateChange}
                    onRefresh={() => refresh(1)}
                  />
                  <DistributorAchRecovery
                    key={`ach:${distributorId}`}
                    distributorId={distributorId}
                    fundingDisabled={
                      cashLocked ||
                      busy ||
                      entryState.busy ||
                      entryState.pending ||
                      (fundingPending && fundingMethod !== 'ach')
                    }
                    onFundingPendingChange={(pending) => {
                      setFundingMethod(pending ? 'ach' : null);
                      onFundingPendingChange(pending);
                    }}
                    onRefresh={() => refresh(1)}
                  />
                  <DistributorCardRecovery
                    key={`card:${distributorId}`}
                    distributorId={distributorId}
                    fundingDisabled={
                      cashLocked ||
                      busy ||
                      entryState.busy ||
                      entryState.pending ||
                      (fundingPending && fundingMethod !== 'card')
                    }
                    onFundingPendingChange={(pending) => {
                      setFundingMethod(pending ? 'card' : null);
                      onFundingPendingChange(pending);
                    }}
                    onRefresh={() => refresh(1)}
                  />
                  <section
                    className="me-distributor-admin-card me-distributor-admin-wide space-y-3"
                    aria-label="Recent bank entries"
                  >
                    <h2 className="text-lg font-semibold text-slate-900">Recent entries</h2>
                    {history?.data.length === 0 && <p>No entries yet.</p>}
                    {history?.data.map((row) => (
                      <p key={row._id}>
                        {row.familyKey} · {row.kind} · {row.unitsDelta > 0 ? '+' : ''}
                        {row.unitsDelta} units
                        {row.createdAt && !Number.isNaN(Date.parse(row.createdAt))
                          ? ` · ${new Date(row.createdAt).toLocaleString()}`
                          : ''}
                        {row.sourceReference ? ` · ${row.sourceReference}` : ''}
                        {row.reason ? ` · ${row.reason}` : ''}
                      </p>
                    ))}
                    {history?.total > 20 && (
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          disabled={busy || entryState.busy || loading || page === 1}
                          onClick={() => refresh(page - 1)}
                        >
                          Previous
                        </button>
                        <span>
                          Page {page} of {Math.ceil(history.total / 20)}
                        </span>
                        <button
                          type="button"
                          disabled={
                            busy ||
                            entryState.busy ||
                            loading ||
                            page >= Math.ceil(history.total / 20)
                          }
                          onClick={() => refresh(page + 1)}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </section>
                </>
              )}
            </div>
            <div className="me-distributor-admin-operations me-distributor-admin-recovery">
              <DistributorBankAlerts admin />
            </div>
          </details>
        </>
      )}
      <section aria-label="All distributors">
        <h2 className="mb-2 text-lg font-semibold text-slate-900">All distributors</h2>
        <div className="me-distributor-admin-operations me-distributor-admin-recovery">
          <BankAlertDeliveryQueue />
          <DistributorDeliveryQueue />
          <DistributorAchMonitorHealth />
          <DistributorReleaseMonitorHealth />
          <DistributorOperationalNotices admin />
          <OperationalNoticeDeliveryQueue />
        </div>
      </section>
    </main>
  );
}
